import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { reduxForm, getFormValues } from 'redux-form';

import { GET_ONE, GET_LIST, UPDATE, provider, history } from '../../_utils';
import { TabPanel, PageHeader, SaveButton, CloseButton, SaveStatus, StatusChip } from '../../_Components';
import '../../operators/operators.css';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import Project from './Project';
import Fields from './Fields';
import Comboboxes from './Comboboxes';
import Cards from './Cards';
import Campaigns from './Campaigns';
import Reports from './Reports';
import Databases from './Databases';

const normalizeProject = (data) => {
    if (!data) {
        return data;
    }
    const next = { ...data };
    if (!next.settings) {
        next.settings = {};
    }
    if (!next.settings.integration) {
        next.settings.integration = { type: 'None', settings: {} };
    }
    if (!next.settings.integration.settings) {
        next.settings.integration.settings = {};
    }
    if (next.settings.integration.type === 'AmoCRM') {
        next.settings.integration.type = 'Amocrm';
    }
    if (!next.settings.list) {
        next.settings.list = {};
    }
    next.settings.list.email = next.settings.list.email || [];
    next.settings.list.telegram = next.settings.list.telegram || [];
    next.settings.list.sms = next.settings.list.sms || [];
    next.settings.list.phonebook = next.settings.list.phonebook || [];
    next.settings.email = next.settings.email || [];
    next.settings.sms = next.settings.sms || [];
    next.fields = next.fields || [];
    next.combobox = next.combobox || [];
    next.campaigns = (next.campaigns || []).map((campaign) => {
        if (!campaign || typeof campaign !== 'object') {
            return campaign;
        }
        return {
            ...campaign,
            operators: toRefIds(campaign.operators),
            queues: campaign.queues || [],
        };
    });
    next.cards = next.cards || [];
    next.reports = next.reports || [];
    next.base = next.base || [];
    return next;
};

const toRefId = (value) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    return value._id || value.id || value.QueueId || null;
};

const toRefIds = (values) => (values || []).map(toRefId).filter(Boolean);

const toQueueRefId = (value) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    return value.QueueId || value.id || value._id || value.queueId || null;
};

const toQueueRefIds = (values) => (values || []).map(toQueueRefId).filter(Boolean);

const sanitizeCampaignPayload = (campaign) => {
    if (!campaign || typeof campaign !== 'object') {
        return toRefId(campaign);
    }
    return {
        _id: campaign._id,
        name: campaign.name,
        type: campaign.type,
        status: campaign.status,
        schedule: campaign.schedule,
        queues: toQueueRefIds(campaign.queues),
        operators: toRefIds(campaign.operators),
        project: toRefId(campaign.project)
    };
};

const sanitizeReportPayload = (report) => {
    if (!report || typeof report !== 'object') {
        return toRefId(report);
    }
    const settings = report.settings ? { ...report.settings } : undefined;
    if (settings && settings.campaign) {
        settings.campaign = toRefId(settings.campaign);
    }
    return {
        _id: report._id,
        name: report.name,
        status: report.status,
        settings,
        report: report.report,
        scripts: report.scripts,
        project: toRefId(report.project)
    };
};

const buildProjectPayload = (formValues) => {
    const data = { ...formValues };
    delete data.__v;
    delete data.created;
    delete data.createdAt;
    delete data.modified;
    data.cards = toRefIds(data.cards);
    data.base = toRefIds((data.base || []).filter((item) => item && (item.name || typeof item === 'string')));
    data.users = toRefIds(data.users);
    data.scripts = toRefIds(data.scripts);
    data.campaigns = (data.campaigns || []).map(sanitizeCampaignPayload);
    data.reports = (data.reports || []).map(sanitizeReportPayload);
    data.fields = (data.fields || []).map((field) => {
        const next = { ...field };
        if (next.combobox) {
            next.combobox = toRefId(next.combobox);
        }
        if (!next.type) {
            next.type = 'input';
        }
        return next;
    });
    return data;
};

const ProjectEdit = ({change, dispatch, initialize, initialized, match, formValues, isSaving, dirty, ...props}) => {
    const [operators, setOperators] = useState([]);
    const [queues, setQueues] = useState([]);
    const [menuEl, setMenuEl] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const { status, data } = await provider(
                    GET_ONE,
                    '/projects',
                    match.params.id,
                    { persist: true }
            );
            if (status === 200 && data) {
                initialize(normalizeProject(data));
            }
        };

        const getQueues = async () => {
            const { data } = await provider(
                GET_LIST,
                '/infra/queues',
                {
                    filter: {},
                    sort: { field: 'name', order: 'ASC' },
                    pagination: { page: 1, perPage: 10000 },
                },
                { persist: true }
            );
            setQueues((data || []).map((row) => ({
                ...row,
                id: row.QueueId || row.id || row._id,
                name: row.name || row.DisplayName || row.Name || row.QueueName || row.title || '',
            })));
        };

        const getOperators = async () => {
            const { data } = await provider(
                GET_LIST,
                '/operators',
                {
                    filter: {  },
                    sort: ['time', 'asc'],
                    pagination: { page: 1, perPage: 9999 },
                },
                { persist: true }
            );
            setOperators(data || []);
        };
        if(!initialized){
            fetchData();
            getQueues();
            getOperators();
        }
    }, [match, initialize, initialized]);


    const save = async () => {
        if (!formValues || !formValues._id) {
            return;
        }
        const { status, data } = await provider(
            UPDATE,
            '/projects',
            { id: formValues._id, data: buildProjectPayload(formValues) }
        );
        if (status === 200 && data) {
            initialize(normalizeProject(data));
        }
    };

    const disableProject = async () => {
        if (!formValues || !match.params.id) {
            return;
        }
        const status = formValues.status === 'Disabled' ? 'Active' : 'Disabled';
        change('status', status);
        const { status: responseStatus, data } = await provider(
            UPDATE,
            '/projects',
            {
                id: match.params.id,
                data: buildProjectPayload({ ...formValues, status })
            }
        );
        if (responseStatus === 200 && data) {
            initialize(normalizeProject(data));
        }
    };

    const close = () => {
        history.push('/projects');
    };

    const fieldsCount = (formValues && formValues.fields) ? formValues.fields.length : 0;
    const dictCount = (formValues && formValues.combobox) ? formValues.combobox.length : 0;
    const cardsCount = (formValues && formValues.cards) ? formValues.cards.length : 0;
    const campaignsCount = (formValues && formValues.campaigns) ? formValues.campaigns.length : 0;
    const reportsCount = (formValues && formValues.reports) ? formValues.reports.length : 0;
    const crmType = formValues && formValues.settings && formValues.settings.integration && formValues.settings.integration.type;

    const panels = [
        {
            label: 'Проект',
            element: <Project />
        },
        {
            label: 'Поля',
            count: fieldsCount,
            element: <Fields />
        },
        {
            label: 'Справочники',
            count: dictCount,
            element: <Comboboxes />
        },
        {
            label: 'Карточки',
            count: cardsCount,
            element: <Cards />
        },
        {
            label: 'Компании',
            count: campaignsCount,
            element: <Campaigns operators={operators} queues={queues} />
        },
        {
            label: 'Отчеты',
            count: reportsCount,
            element: <Reports queues={queues}/>
        },
        {
            label: 'Базы',
            element: <Databases projectId={match.params.id} queues={queues} change={change}/>
        }
    ];

    const header = (
        <PageHeader
            leading={(
                <button type="button" className="pc-emp-btn" onClick={close}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path d="M13 8H4M7.5 4.5L4 8l3.5 3.5" />
                    </svg>
                    Все проекты
                </button>
            )}
            chips={(
                <>
                    <StatusChip tone={formValues && formValues.status === 'Active' ? 'ok' : 'danger'} dot>
                        {formValues && formValues.status === 'Active' ? 'Активен' : 'Заблокирован'}
                    </StatusChip>
                    {crmType && crmType !== 'None' && (
                        <StatusChip tone="mono">{crmType}</StatusChip>
                    )}
                </>
            )}
            actions={(
                <>
                    <SaveStatus dirty={!!dirty} />
                    <SaveButton handleSaveClick={save} isSaving={isSaving} disabled={!formValues || !dirty} />
                    <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)} title="Ещё">
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
                        <MenuItem onClick={() => { setMenuEl(null); disableProject(); }}>
                            {formValues && formValues.status === 'Active' ? 'Заблокировать' : 'Разблокировать'}
                        </MenuItem>
                    </Menu>
                    <CloseButton handleCloseClick={close} />
                </>
            )}
        />
    );

    return formValues
        ? <TabPanel header={header} panels={panels} />
        : header;
};


const mapStateToProps = (state, props) => {
    const formValues = getFormValues('project')(state);
    const { fetch } = state;
    return {
        formValues,
        isSaving: fetch ? fetch.creat > 0 : false
    };
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'project',
    })
);

export default enhance(ProjectEdit);
